import { defineStore } from 'pinia'
import moment from 'moment'
import {
    PRICING_ACTIONS_APPROVAL,
    DASHBOARD_PRICING_ANALYSIS,
    DASHBOARD_PRICING_ANALYSIS_DETAILS,
    DASHBOARD_PURCHASE_RESEARCH_UPDATE_PURCHASED,
} from '@/data/url'
import apiClient from '@/services/apiClient'
import { getApiError, isApiSuccess, unwrapApiData } from '@/services/apiEnvelope'

const defaultDetailData = () => ({
    quotes: [],
    sales: [],
    open_pos: [],
    open_sos: [],
})

const defaultRange = () => [moment().subtract(1, 'weeks').toDate(), moment().toDate()]

const defaultState = () => ({
    queryDataRaw: [],
    detailDataRaw: defaultDetailData(),
    pricingActionData: [],
    customer: '',
    part: '',
    customer_searched: '',
    date_range: 'week',
    range: defaultRange(),
    has_pricing_action: true,
    criteria_ignored: true,
    not_reviewed: true,
    research_parts_list: [],
    loading: false,
    error: null,
})

export const useDashPricingAnalysisStore = defineStore('dashPricingAnalysis', {
    state: () => ({
        ...defaultState(),
    }),
    actions: {
        setDashPricingAnalysis(dash = {}) {
            this.customer = dash.customer ?? ''
            this.part = dash.part ?? ''
            this.queryDataRaw = Array.isArray(dash.queryDataRaw) ? dash.queryDataRaw : []
            this.detailDataRaw = dash.detailDataRaw ?? defaultDetailData()
            this.date_range = dash.date_range ?? 'week'
            this.range = Array.isArray(dash.range) ? dash.range : defaultRange()
            this.research_parts_list = Array.isArray(dash.research_parts_list)
                ? dash.research_parts_list
                : []
        },
        resetDashPricingAnalysis() {
            Object.assign(this, defaultState())
        },
        resetDetailData() {
            this.detailDataRaw = defaultDetailData()
        },
        setPricingActionRow(paRow) {
            if (!paRow || !Object.prototype.hasOwnProperty.call(paRow, 'id')) return
            const id = paRow.id
            const index = this.pricingActionData.findIndex((row) => row.id === id)
            if (index !== -1) {
                const copy = [...this.pricingActionData]
                copy.splice(index, 1, paRow)
                this.pricingActionData = copy
            } else {
                this.pricingActionData = [...this.pricingActionData, paRow]
            }
        },
        applyPurchaseUpdates(changedData = []) {
            if (!Array.isArray(changedData) || !changedData.length) return
            const updated = this.queryDataRaw.map((row) => {
                const match = changedData.find((entry) => entry.id === row.id)
                return match
                    ? {
                          ...row,
                          purchased: match.purchased,
                          no_purchase: match.no_purchase,
                      }
                    : row
            })
            this.queryDataRaw = updated
        },
        setResearchPartsList(parts = []) {
            this.research_parts_list = Array.isArray(parts) ? parts : []
        },
        async fetchDashboardPricingAnalysis({
            time_created_begin,
            time_created_end,
            customer = this.customer,
            part_number = this.part,
            has_pricing_action = this.has_pricing_action,
            criteria_ignored = this.criteria_ignored,
            not_reviewed = this.not_reviewed,
        } = {}) {
            this.loading = true
            this.error = null
            this.queryDataRaw = []

            try {
                const response = await apiClient.post(DASHBOARD_PRICING_ANALYSIS, {
                    time_created_begin,
                    time_created_end,
                    customer,
                    part_number,
                    has_pricing_action,
                    criteria_ignored,
                    not_reviewed,
                })

                const data = unwrapApiData(response, [])
                if (isApiSuccess(response)) {
                    this.queryDataRaw = data || []
                    this.customer_searched = customer || ''
                } else {
                    this.queryDataRaw = []
                }

                return response.data
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to fetch dashboard pricing analysis', error)
                throw error
            } finally {
                this.loading = false
            }
        },
        async fetchPricingDetails({ part_number_list = [], customer = this.customer } = {}) {
            this.loading = true
            this.error = null
            this.resetDetailData()
            this.pricingActionData = []

            try {
                const response = await apiClient.post(DASHBOARD_PRICING_ANALYSIS_DETAILS, {
                    part_number_list,
                    customer,
                })

                const detailData = unwrapApiData(response, {})
                if (isApiSuccess(response)) {
                    this.detailDataRaw = {
                        quotes: detailData.quotes || [],
                        sales: detailData.sales || [],
                        open_pos: detailData.open_pos || [],
                        open_sos: detailData.open_sos || [],
                    }
                    this.pricingActionData = detailData.pricing_actions || []
                } else {
                    this.resetDetailData()
                    this.pricingActionData = []
                }

                return response.data
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to fetch pricing details', error)
                throw error
            } finally {
                this.loading = false
            }
        },
        async approvePricingAction(pricingAction = {}) {
            this.loading = true
            this.error = null

            try {
                const response = await apiClient.post(PRICING_ACTIONS_APPROVAL, pricingAction)

                const data = unwrapApiData(response, {})
                if (isApiSuccess(response) && data) {
                    this.setPricingActionRow(data)
                    return data
                }

                throw new Error(getApiError(response, 'Failed to create pricing action'))
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to approve pricing action', error)
                throw error
            } finally {
                this.loading = false
            }
        },
        async updatePurchaseResearch({ id_list = [], purchased = null, no_purchase = null } = {}) {
            this.loading = true
            this.error = null

            try {
                const response = await apiClient.post(
                    DASHBOARD_PURCHASE_RESEARCH_UPDATE_PURCHASED,
                    {
                        id_list,
                        purchased,
                        no_purchase,
                    }
                )

                const data = unwrapApiData(response, [])
                if (isApiSuccess(response) && Array.isArray(data)) {
                    this.applyPurchaseUpdates(data)
                }

                return response.data
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to update purchase research', error)
                throw error
            } finally {
                this.loading = false
            }
        },
    },
})
